(() => {
  if (window.SNCollectionShowcase) { window.SNCollectionShowcase.init(document); return; }
  const waiting = new Set();
  let deferredObserver;
  const unqueue = root => {
    waiting.delete(root);
    deferredObserver?.unobserve(root);
    if (!waiting.size) { deferredObserver?.disconnect(); deferredObserver = null; }
  };
  const init = root => {
    if (!root?.isConnected || root.dataset.snCshowReady) return;
    root.dataset.snCshowReady = 'true';
    const events = new AbortController();
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal: events.signal });
    let disposed = false, frame;
    const tabs = [...root.querySelectorAll('[data-sn-cshow-tab]')];
    const panels = [...root.querySelectorAll('[data-sn-cshow-panel]')];
    const heroes = [...root.querySelectorAll('[data-sn-cshow-hero]')];
    const mobileLimit = Number(root.dataset.mobileProducts) || 2;
    const media = window.matchMedia('(max-width:749px)');
    let inViewport = !('IntersectionObserver' in window);

    const currentVideo = hero => {
      if (!hero) return null;
      const mobile = window.matchMedia('(max-width:749px)').matches;
      const selector = mobile
        ? '.sn-cshow__media-item--mobile [data-sn-cshow-video]'
        : '.sn-cshow__media-item--desktop [data-sn-cshow-video]';
      return hero.querySelector(selector);
    };

    const hydrateVideo = video => {
      if (!video || video.dataset.loaded === 'true') return;
      if (video.dataset.poster) video.poster = video.dataset.poster;
      video.querySelectorAll('source[data-src]').forEach(source => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.loaded = 'true';
      video.load();
    };

    const pauseHero = hero => {
      hero?.querySelectorAll('[data-sn-cshow-video]').forEach(video => {
        video.pause();
      });
    };

    const activateHero = id => {
      if (disposed || !root.isConnected) return;
      let activeHero = null;
      heroes.forEach(hero => {
        const active = hero.dataset.snCshowHero === id;
        hero.classList.toggle('is-active', active);
        hero.hidden = !active;
        if (active) activeHero = hero;
        else pauseHero(hero);
      });
      const video = currentVideo(activeHero);
      activeHero?.querySelectorAll('[data-sn-cshow-video]').forEach(other => { if (other !== video) other.pause(); });
      if (!video || !inViewport || document.hidden) return;
      hydrateVideo(video);
      video.muted = video.dataset.startMuted !== 'false';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (video.dataset.autoplay === 'true' && !reducedMotion) {
        const result = video.play();
        if (result?.catch) result.catch(() => {});
      }
    };

    root.querySelectorAll('[data-sn-cshow-video]').forEach(video => {
      on(video, 'click', () => {
        hydrateVideo(video);
        if (video.paused) {
          const result = video.play();
          if (result?.catch) result.catch(() => {});
        } else {
          video.pause();
        }
      });
    });

    const applyProductLimit = () => {
      const isMobile = window.matchMedia('(max-width:749px)').matches;
      root.querySelectorAll('[data-sn-cshow-product]').forEach(card => {
        const index = Number(card.dataset.productIndex);
        card.hidden = isMobile && index > mobileLimit && !card.closest('[data-sn-cshow-panel]').classList.contains('is-expanded');
      });
    };

    tabs.forEach(tab => {
      on(tab, 'click', () => {
        const id = tab.dataset.snCshowTab;
        tabs.forEach(item => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => {
          const active = panel.dataset.snCshowPanel === id;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });
        activateHero(id);
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (disposed || !root.isConnected) return;
          const nav = tab.parentElement;
          nav.scrollTo({ left: tab.offsetLeft - ((nav.clientWidth - tab.offsetWidth) / 2), behavior: 'smooth' });
        });
      });
      on(tab, 'keydown', event => {
        if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[index].click(); tabs[index].focus({ preventScroll: true });
      });
    });

    root.querySelectorAll('[data-sn-cshow-expand]').forEach(button => {
      const panel = button.closest('[data-sn-cshow-panel]');
      const label = button.querySelector('[data-sn-cshow-expand-label]');
      const expandLabel = label?.textContent || window.snStrings?.showMore || '';
      on(button, 'click', () => {
        const expanded = !panel.classList.contains('is-expanded');
        panel.classList.toggle('is-expanded', expanded);
        applyProductLimit();
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (label) label.textContent = expanded ? (root.dataset.collapseLabel || window.snStrings?.showLess || '') : expandLabel;
        if (!expanded) panel.querySelector('.sn-cshow__products')?.scrollTo({ left: 0, behavior: 'smooth' });
      });
    });

    applyProductLimit();
    if (tabs[0]) activateHero(tabs[0].dataset.snCshowTab);
    const onDeviceChange = () => {
      applyProductLimit();
      const activeTab = tabs.find(tab => tab.classList.contains('is-active'));
      if (activeTab) activateHero(activeTab.dataset.snCshowTab);
    };
    const onVisibility = () => {
      if (document.hidden) heroes.forEach(pauseHero);
      else onDeviceChange();
    };
    const viewportObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      inViewport = entries.some(entry => entry.isIntersecting);
      if (inViewport) onDeviceChange();
      else heroes.forEach(pauseHero);
    }) : null;
    viewportObserver?.observe(root);
    if (media.addEventListener) media.addEventListener('change', onDeviceChange);
    else media.addListener(onDeviceChange);
    document.addEventListener('visibilitychange', onVisibility);
    const unload = event => {
      if (!event.target.contains(root)) return;
      disposed = true; events.abort(); cancelAnimationFrame(frame);
      heroes.forEach(pauseHero);
      viewportObserver?.disconnect();
      if (media.removeEventListener) media.removeEventListener('change', onDeviceChange);
      else media.removeListener(onDeviceChange);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('shopify:section:unload', unload);
      delete root.dataset.snCshowReady;
    };
    document.addEventListener('shopify:section:unload', unload);
  };

  const initAll = scope => {
    const roots = [...scope.querySelectorAll('[data-sn-cshow]')];
    if (scope.matches?.('[data-sn-cshow]')) roots.unshift(scope);
    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      roots.forEach(root => { unqueue(root); init(root); });
      return;
    }
    waiting.forEach(root => { if (!root.isConnected) unqueue(root); });
    roots.forEach(root => {
      if (!root.isConnected || root.dataset.snCshowReady || waiting.has(root)) return;
      if (!deferredObserver) deferredObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!waiting.has(entry.target)) return;
          if (!entry.target.isConnected) { unqueue(entry.target); return; }
          if (!entry.isIntersecting) return;
          unqueue(entry.target); init(entry.target);
        });
      }, { rootMargin: '300px 0px' });
      waiting.add(root); deferredObserver.observe(root);
    });
  };
  window.SNCollectionShowcase = { init: initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    waiting.forEach(root => { if (event.target.contains(root)) unqueue(root); });
  });
})();
